import { createContext, ReactNode, useContext, useReducer } from 'react';
import { MoldOrder } from '../types/mold';

interface MoldOrderState {
  moldOrders: MoldOrder[];
  loading: boolean;
  error: string | null;
}

type MoldOrderAction =
  | { type: 'FETCH_MOLD_ORDERS_START' }
  | { type: 'FETCH_MOLD_ORDERS_SUCCESS'; payload: MoldOrder[] }
  | { type: 'FETCH_MOLD_ORDERS_ERROR'; payload: string }
  | { type: 'CREATE_MOLD_ORDER_START' }
  | { type: 'CREATE_MOLD_ORDER_SUCCESS'; payload: MoldOrder }
  | { type: 'CREATE_MOLD_ORDER_ERROR'; payload: string }
  | {
      type: 'SUBMIT_MOLD_ORDER_ACTION_SUCCESS';
      payload: { id: number; action: string };
    };

interface MoldOrderContextType {
  state: MoldOrderState;
  dispatch: React.Dispatch<MoldOrderAction>;
}

const initialState: MoldOrderState = {
  moldOrders: [],
  loading: false,
  error: null,
};

const MoldOrderContext = createContext<MoldOrderContextType | undefined>(
  undefined,
);

const moldOrderReducer = (
  state: MoldOrderState,
  action: MoldOrderAction,
): MoldOrderState => {
  switch (action.type) {
    case 'FETCH_MOLD_ORDERS_START':
      return { ...state, loading: true };
    case 'FETCH_MOLD_ORDERS_SUCCESS':
      return { ...state, loading: false, moldOrders: action.payload };
    case 'FETCH_MOLD_ORDERS_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'CREATE_MOLD_ORDER_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'CREATE_MOLD_ORDER_SUCCESS':
      return {
        ...state,
        loading: false,
        moldOrders: [...state.moldOrders, action.payload],
        error: null,
      };
    case 'CREATE_MOLD_ORDER_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'SUBMIT_MOLD_ORDER_ACTION_SUCCESS':
    default:
      return state;
  }
};

export const MoldOrderProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(moldOrderReducer, initialState);

  return (
    <MoldOrderContext.Provider value={{ state, dispatch }}>
      {children}
    </MoldOrderContext.Provider>
  );
};

export const useMoldOrderContext = () => {
  const context = useContext(MoldOrderContext);
  if (context === undefined) {
    throw new Error('useMoldContext must be used within a MoldProvider');
  }
  return context;
};
